// Renders deterministic allergy/duplicate-therapy warnings from
// backend/utils/rxSafetyChecks.js — the OP prescribing/dispensing
// counterpart to features/ip-case-sheet/AlertStrip.tsx's IP alerts. Both now
// map onto the same components/ui/AlertStrip, so an alert reads identically
// wherever it appears; this file is only the mapping from the rxSafetyChecks
// row shape, and the advisory wording specific to prescribing.
//
// Shared here rather than in either feature folder because both
// PrescriptionBuilder.tsx (consultation) and DispensingTab.tsx (pharmacy)
// need the identical rendering for the identical alert shape — matching how
// VoiceMicButton/VoiceFormFiller are already shared cross-feature from this
// same directory.
//
// ── The one rule about this component ────────────────────────────
// These are ADVISORY ONLY, computed by plain functions over rows before any
// AI call — nothing here is a reason to disable a save or a sale button.
// Callers must never wire `alerts.length > 0` into a `disabled` prop; this
// component exists to inform the decision, not to make it.
import { AlertStrip, type StripAlert } from '@/components/ui/AlertStrip';
import { cn } from '@/lib/cn';

export interface SafetyAlertLike {
  rule_key: string;
  label: string;
  severity: 'red' | 'amber' | 'info';
  message: string;
  detail?: string | null;
  clinical_basis: string;
  rule_version: number;
}

const ADVISORY = 'Advisory only — review and decide. This never blocks saving or dispensing.';

export default function SafetyAlertBanner({ alerts, className }: { alerts: SafetyAlertLike[]; className?: string }) {
  const mapped: StripAlert[] = alerts.map((a, i) => ({
    key: `${a.rule_key}-${i}`,
    label: a.label,
    severity: a.severity,
    message: a.message,
    detail: a.detail,
    basis: a.clinical_basis,
    basisMeta: `rule v${a.rule_version}`,
    advisoryNote: ADVISORY,
  }));
  // `collapseAfter={false}`: these are checked against one prescription's own
  // drug list, so the list is short and bounded by construction — collapsing
  // it would hide a duplicate-therapy warning behind a click for no gain.
  return <AlertStrip alerts={mapped} collapseAfter={false} className={cn('mb-2.5', className)} />;
}
